import React from 'react';

import { Navigate, Outlet, useLocation, useParams } from 'react-router-dom';

import { Box, Container } from '@mui/material';

import { useGetProperty } from '~/hooks/apis/accommodation/useGetProperty';
import { useGetOffer } from '~/hooks/apis/offers/useGetOffer';

import { isFlashCampaignType, isOfferTypeHotel } from '~/utils/offer';

import type { OfferNavigationSnackbarState } from '../offerNavigationState';

import { FLASH_ONLY_OFFER_BUILDER_MESSAGE } from './Account/accountRelatedModel';
import { type OfferBuilderProperty, formatPropertyLocation } from './Content/PropertyInformation';
import OfferBuilderSidebar from './OfferBuilderSidebar';
import { useSidebarCollapsed } from './shared/useSidebarCollapsed';

const WORKFLOW_ITEM_BY_PATH: Record<string, string> = {
  assets: 'Assets',
  'cm-checklist': 'CM Checklist',
  content: 'Content',
  'deal-information': 'Deal Information',
  'hotel-proposal-pricing-tool': 'Hotel Proposal Pricing Tool',
  'rates-and-inventory': 'Rates and Inventory',
};

export default function OfferBuilderShell() {
  const { id_offer: offerId } = useParams<{ id_offer: string }>();
  const { pathname } = useLocation();
  const { data: offer, isFetched: isOfferFetched } = useGetOffer(offerId);
  const { isSidebarCollapsed, toggleSidebarCollapsed } = useSidebarCollapsed();
  const isHotelOfferType = !!offer && isOfferTypeHotel(offer);
  const isFlashOfferType = isFlashCampaignType(offer?.campaign_type);
  const isSupportedOfferType = isHotelOfferType && isFlashOfferType;
  const propertyId = isSupportedOfferType
    ? offer?.packages?.find((offerPackage) => offerPackage?.fk_property_id)?.fk_property_id
    : undefined;
  const { data: property } = useGetProperty<OfferBuilderProperty>({
    propertyId,
    enabled: isSupportedOfferType,
  });
  const activePath = pathname.split('/').filter(Boolean).pop() ?? 'content';
  const activeItem = WORKFLOW_ITEM_BY_PATH[activePath] ?? 'Content';
  const hotelName = property?.name || propertyId || 'Property';
  const propertyLocation = formatPropertyLocation(property);

  // `offer` (not just `isOfferFetched`) must be truthy so a fetch failure falls through to
  // the child route's own error state instead of being redirected away.
  if (isOfferFetched && offer && offerId) {
    if (!isSupportedOfferType) {
      const state: OfferNavigationSnackbarState = {
        snackbar: { message: FLASH_ONLY_OFFER_BUILDER_MESSAGE, variant: 'warning' },
      };

      return <Navigate to={`/offers/${offerId}`} replace state={state} />;
    }

    if (offer.vendor_account_id && !propertyId) {
      return <Navigate to={`/vendors/${offer.vendor_account_id}?setupFlashOffer=${offerId}`} replace />;
    }
  }

  return (
    <Container sx={{ maxWidth: 1800 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: isSidebarCollapsed ? '56px minmax(0, 1fr)' : '280px minmax(0, 1fr)' },
        }}
      >
        <OfferBuilderSidebar
          accountId={offer?.vendor_account_id}
          activeItem={activeItem}
          collapsed={isSidebarCollapsed}
          hotelCode={property?.hotel_code}
          hotelName={hotelName}
          location={propertyLocation}
          offerId={offerId}
          onToggleCollapsed={toggleSidebarCollapsed}
        />

        <Box
          sx={{
            '--offer-builder-action-left': isSidebarCollapsed ? '112px' : '336px',
            minWidth: 0,
            px: { xs: 2, md: 4 },
            py: 3,
            pb: 14,
          }}
        >
          <Outlet />
        </Box>
      </Box>
    </Container>
  );
}
